// =============================================================
// FLOW: Reports & dashboard — a plant-wide maintenance-health overview.
//
// Ports the prototype SecDashboard (scripts/perpetuum-desktop-sections2.jsx)
// onto the REAL /api/v1/reports/* aggregation. EVERY number on this screen is
// read from a live report — nothing is synthesised. Where a report doesn't
// expose a datum (e.g. there is no per-asset time-series endpoint), we show
// "n/a" / an EmptyState rather than invent a value.
//
// Report → widget mapping (shapes verified against ReportingService):
//   • mtbf          metrics: MTBF (h), Failures, Observed window (h),
//                   Total downtime (h), Availability (ratio);
//                   rows: [deviceId, failures, downtimeHours].
//   • tasks-repairs metrics: Total tasks, Completed, Open,
//                   Mean completion lead time (h);
//                   rows: [number, title, deviceId, status, priority, completedAt].
//   • parts-spend   metrics: Purchase spend (currency),
//                   Consumption value (write-offs) (currency), Movements.
//   • downtime      metrics: Total downtime (h), Episodes.
//
// KPI cards    ← Availability % (mtbf), MTBF h (mtbf),
//                On-time/completion % (tasks-repairs Completed÷Total),
//                Parts spend € (parts-spend Purchase spend).
// Stacked bars ← tasks-repairs rows bucketed by completion month, split
//                planned (normal/low priority) vs unplanned (critical/high) —
//                a real reactive-vs-preventive proxy from the live priority field.
// Donut        ← mtbf rows: downtime hours per device (downtime by asset/line).
// Top assets   ← mtbf rows sorted by downtime hours, with real failures +
//                downtime-hour totals (the prototype's per-asset sparkline has no
//                backing time-series endpoint, so we show the real totals instead
//                of a fabricated trend — see the SUMMARY deferral note).
// =============================================================

import { useMemo } from "react";
import type { ReportFilters, ReportResult } from "@perpetuum/core";
import { useReport } from "../lib/queries.js";
import { useDevices } from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Icon, ICONS } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";
import { BarChart, Donut, type BarChartBar, type DonutSlice } from "../components/charts.js";

const NO_FILTERS: ReportFilters = {};

/** Find a metric value by its (server-defined) label; null when absent/N-A. */
function metric(report: ReportResult | undefined, label: string): number | null {
  if (!report) return null;
  const m = report.metrics.find((x) => x.label === label);
  return m ? m.value : null;
}

/** Donut palette — cycles status tokens, like the prototype's by-line colours. */
const DONUT_COLORS = [
  "var(--status-critical)",
  "var(--status-warning)",
  "var(--status-info)",
  "var(--status-success)",
  "var(--status-paused)",
] as const;

const PLANNED_COLOR = "var(--status-success)";
const UNPLANNED_COLOR = "var(--status-critical)";

/** Format a number for a KPI value, or the n/a token when null. */
function fmt(value: number | null, digits = 0): string | null {
  if (value == null) return null;
  return value.toFixed(digits);
}

export function Dashboard() {
  const t = useScopedT();

  // Each KPI/chart is fed by a real report. Independent queries so a slow/empty
  // one never blocks the others.
  const mtbfQ = useReport("mtbf", NO_FILTERS);
  const tasksQ = useReport("tasks-repairs", NO_FILTERS);
  const partsQ = useReport("parts-spend", NO_FILTERS);
  const devicesQ = useDevices();

  const mtbf = mtbfQ.data;
  const tasksR = tasksQ.data;
  const parts = partsQ.data;

  const deviceName = useMemo(() => {
    const map = new Map((devicesQ.data ?? []).map((d) => [d.id, d.name]));
    return (id: string) => map.get(id) ?? id;
  }, [devicesQ.data]);

  // ── KPI values (all live; null → render n/a) ───────────────────────────────
  const availabilityRatio = metric(mtbf, "Availability"); // 0..1
  const availabilityPct = availabilityRatio == null ? null : availabilityRatio * 100;
  const mtbfHours = metric(mtbf, "MTBF");
  const totalTasks = metric(tasksR, "Total tasks");
  const completedTasks = metric(tasksR, "Completed");
  const completionPct =
    totalTasks != null && completedTasks != null && totalTasks > 0 ? (completedTasks / totalTasks) * 100 : null;
  const purchaseSpend = metric(parts, "Purchase spend");

  // ── Stacked bars: planned vs unplanned, bucketed by completion month ───────
  // Built from tasks-repairs rows (columns: number,title,deviceId,status,priority,completedAt).
  const repairBars: BarChartBar[] = useMemo(() => {
    if (!tasksR) return [];
    const ci = {
      priority: tasksR.columns.indexOf("priority"),
      completedAt: tasksR.columns.indexOf("completedAt"),
    };
    if (ci.priority < 0 || ci.completedAt < 0) return [];
    // month key (YYYY-MM) → { planned, unplanned }
    const byMonth = new Map<string, { planned: number; unplanned: number }>();
    for (const row of tasksR.rows) {
      const completedAt = row[ci.completedAt];
      if (completedAt == null) continue; // only completed work has a planned/unplanned outcome
      const d = new Date(String(completedAt));
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const prio = String(row[ci.priority] ?? "normal");
      const unplanned = prio === "critical" || prio === "high";
      const cur = byMonth.get(key) ?? { planned: 0, unplanned: 0 };
      if (unplanned) cur.unplanned += 1;
      else cur.planned += 1;
      byMonth.set(key, cur);
    }
    return [...byMonth.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6) // last 6 months, like the prototype's 6-week window
      .map(([month, v]) => ({
        label: month.slice(2), // YY-MM compact tick
        segments: [
          { value: v.planned, color: PLANNED_COLOR, label: t("dashboard.planned") },
          { value: v.unplanned, color: UNPLANNED_COLOR, label: t("dashboard.unplanned") },
        ],
      }));
  }, [tasksR, t]);

  // ── Donut + top assets: per-device downtime hours (mtbf rows) ──────────────
  // mtbf rows columns: [deviceId, failures, downtimeHours].
  const perDevice = useMemo(() => {
    if (!mtbf) return [];
    const ci = {
      deviceId: mtbf.columns.indexOf("deviceId"),
      failures: mtbf.columns.indexOf("failures"),
      downtimeHours: mtbf.columns.indexOf("downtimeHours"),
    };
    if (ci.deviceId < 0 || ci.downtimeHours < 0) return [];
    return mtbf.rows
      .map((row) => ({
        deviceId: String(row[ci.deviceId] ?? "—"),
        failures: ci.failures >= 0 ? Number(row[ci.failures] ?? 0) : 0,
        hours: Number(row[ci.downtimeHours] ?? 0),
      }))
      .filter((r) => r.hours > 0 || r.failures > 0)
      .sort((a, b) => b.hours - a.hours);
  }, [mtbf]);

  const donutSlices: DonutSlice[] = useMemo(
    () =>
      perDevice.slice(0, 5).map((r, i) => ({
        label: deviceName(r.deviceId),
        value: r.hours,
        color: DONUT_COLORS[i % DONUT_COLORS.length] ?? DONUT_COLORS[0],
        display: `${r.hours.toFixed(1)} h`,
      })),
    [perDevice, deviceName],
  );
  const totalDowntimeH = perDevice.reduce((s, r) => s + r.hours, 0);
  const topAssets = perDevice.slice(0, 6);

  const kpiLoading = mtbfQ.isLoading || tasksQ.isLoading || partsQ.isLoading;

  return (
    <div>
      <h2 className="d-h2">{t("dashboard.title")}</h2>
      <p className="d-sub">{t("dashboard.sub")}</p>

      {/* KPI row */}
      {kpiLoading ? (
        <Skeleton variant="card" count={4} height={92} className="dash-kpi-sk" />
      ) : (
        <div className="kpi-strip" style={{ marginBottom: 20 }} data-testid="dash-kpis">
          <Kpi
            icon={ICONS.refresh}
            value={fmt(availabilityPct, 1)}
            unit="%"
            label={t("dashboard.kpi.uptime")}
            naHint={t("dashboard.naHint")}
            testId="dash-availability"
          />
          <Kpi
            icon={ICONS.clock}
            value={fmt(mtbfHours, 0)}
            unit="h"
            label={t("dashboard.kpi.mtbf")}
            naHint={t("dashboard.naHint")}
            testId="dash-mtbf"
          />
          <Kpi
            icon={ICONS.check}
            value={fmt(completionPct, 0)}
            unit="%"
            label={t("dashboard.kpi.completion")}
            naHint={t("dashboard.naHint")}
            testId="dash-completion"
          />
          <Kpi
            icon={ICONS.box}
            value={purchaseSpend == null ? null : purchaseSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            unit="€"
            unitBefore
            label={t("dashboard.kpi.partsSpend")}
            naHint={t("dashboard.naHint")}
            testId="dash-parts-spend"
          />
        </div>
      )}

      <div className="d-grid-2" style={{ gridTemplateColumns: "1.5fr 1fr" }}>
        {/* Planned vs unplanned repairs */}
        <div className="d-card">
          <div className="d-card-hd">
            <h3>{t("dashboard.repairs")}</h3>
            <span style={{ flex: 1 }} />
            <div className="bc-legend">
              <span>
                <span className="bc-sw" style={{ background: PLANNED_COLOR }} aria-hidden="true" />
                {t("dashboard.planned")}
              </span>
              <span>
                <span className="bc-sw" style={{ background: UNPLANNED_COLOR }} aria-hidden="true" />
                {t("dashboard.unplanned")}
              </span>
            </div>
          </div>
          <div className="d-card-body">
            {tasksQ.isLoading ? (
              <Skeleton variant="card" height={180} />
            ) : repairBars.length === 0 ? (
              <EmptyState compact icon={ICONS.list} title={t("dashboard.noRepairs")} />
            ) : (
              <BarChart
                bars={repairBars}
                unit=""
                ariaLabel={`${t("dashboard.repairs")}: ${repairBars
                  .map(
                    (b) =>
                      `${b.label} ${t("dashboard.planned")} ${b.segments[0]?.value ?? 0}, ${t("dashboard.unplanned")} ${b.segments[1]?.value ?? 0}`,
                  )
                  .join("; ")}`}
              />
            )}
          </div>
        </div>

        {/* Downtime by asset/line */}
        <div className="d-card">
          <div className="d-card-hd">
            <h3>{t("dashboard.downtimeByLine")}</h3>
          </div>
          <div className="d-card-body">
            {mtbfQ.isLoading ? (
              <Skeleton variant="card" height={180} />
            ) : donutSlices.length === 0 ? (
              <EmptyState compact icon={ICONS.down} title={t("dashboard.noDowntime")} />
            ) : (
              <Donut
                slices={donutSlices}
                centerValue={`${totalDowntimeH.toFixed(1)}h`}
                centerLabel={t("dashboard.totalDowntime")}
                ariaLabel={`${t("dashboard.downtimeByLine")}: ${donutSlices.map((s) => `${s.label} ${s.display}`).join(", ")}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Top assets by maintenance load */}
      <div className="d-card" style={{ marginTop: 18 }}>
        <div className="d-card-hd">
          <h3>{t("dashboard.topAssets")}</h3>
          <span style={{ flex: 1 }} />
          <span className="dict-hint">{t("dashboard.topAssetsHint")}</span>
        </div>
        <div className="d-card-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
          {mtbfQ.isLoading ? (
            <Skeleton variant="text" count={4} />
          ) : topAssets.length === 0 ? (
            <EmptyState compact icon={ICONS.wrench} title={t("dashboard.noAssets")} />
          ) : (
            <ul className="dash-assets" data-testid="dash-top-assets">
              {topAssets.map((a) => {
                const maxH = topAssets[0]?.hours || 1;
                return (
                  <li className="dash-asset" key={a.deviceId}>
                    <span className="dash-asset-nm">
                      {deviceName(a.deviceId)}
                      <small>{t("dashboard.assetMeta", { failures: a.failures })}</small>
                    </span>
                    <span
                      className="dash-asset-bar"
                      role="img"
                      aria-label={t("dashboard.assetLoad", { hours: a.hours.toFixed(1) })}
                    >
                      <span
                        className="dash-asset-fill"
                        style={{
                          width: `${Math.max(4, (a.hours / maxH) * 100)}%`,
                          background: a.hours >= maxH * 0.66 ? "var(--status-critical)" : a.hours >= maxH * 0.33 ? "var(--status-warning)" : "var(--status-success)",
                        }}
                      />
                    </span>
                    <span className="dash-asset-val">{a.hours.toFixed(1)} h</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/** One KPI card; renders the n/a token (with a hint) when value is null. */
function Kpi({
  icon,
  value,
  unit,
  unitBefore = false,
  label,
  naHint,
  testId,
}: {
  icon: string;
  value: string | null;
  unit: string;
  unitBefore?: boolean;
  label: string;
  naHint: string;
  testId: string;
}) {
  const t = useScopedT();
  const na = value == null;
  return (
    <div className="kpi" title={na ? naHint : undefined}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--sys-fg-3)", marginBottom: 6 }}>
        <Icon d={icon} size={16} />
      </div>
      <div className="v" data-testid={testId}>
        {na ? (
          t("dashboard.na")
        ) : (
          <>
            {unitBefore && <span className="kpi-unit" style={{ marginRight: 1 }}>{unit}</span>}
            {value}
            {!unitBefore && <span className="kpi-unit">{unit}</span>}
          </>
        )}
      </div>
      <div className="k">{label}</div>
    </div>
  );
}
